import { addCallback } from '../../../lib/teact/teactn';

import type { ActionReturnType, GlobalState } from '../../types';
import { type LangCode, LeftColumnContent, SettingsScreens } from '../../../types';

import { requestMutation } from '../../../lib/fasterdom/fasterdom';
import { IS_IOS } from '../../../util/browser/windowEnvironment';
import { disableDebugConsole, initDebugConsole } from '../../../util/debugConsole';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { setTimeFormat as setLocalizedTimeFormat } from '../../../util/localization';
import { isModFeatureEnabled, setModFeatureCache } from '../../../util/modFeatures';
import {
  canUsePaidModFeature,
  canUsePaidPrivateMode,
  getRasiTechAccessUserId,
  logRasiActivity,
  PAID_MOD_FEATURES,
  PAID_PRIVATE_MODE_KEYS,
} from '../../../util/rasitechAccess';
import { oldSetLanguage, setTimeFormat as setLegacyTimeFormat } from '../../../util/oldLangProvider';
import { applyPerformanceSettings } from '../../../util/perfomanceSettings';
import switchTheme from '../../../util/switchTheme';
import { updatePeerColors } from '../../../util/theme';
import { callApi, setShouldEnableDebugLog } from '../../../api/gramjs';
import { setIsPremium } from '../../../api/gramjs/methods/client';
import { setRasiTechModulesCache } from '../../../util/modModules';
import { forceUpdateCache } from '../../cache';
import { addTabStateResetterAction } from '../../helpers/meta';
import {
  addActionHandler, getActions, getGlobal, setGlobal,
} from '../../index';
import { INITIAL_PERFORMANCE_STATE_MAX, INITIAL_PERFORMANCE_STATE_MIN } from '../../initialState';
import { replaceSettings, updateSharedSettings, updateThemeSettings } from '../../reducers';
import { updateTabState } from '../../reducers/tabs';
import {
  selectChatFolder, selectTabState,
} from '../../selectors';
import { selectRasiTechModules, selectRasiTechSettings, selectSharedSettings } from '../../selectors/sharedState';

let prevGlobal: GlobalState | undefined;

addCallback((global: GlobalState) => {
  const { updatePageTitle, updateShouldDebugExportedSenders, updateShouldEnableDebugLog } = getActions();

  const oldGlobal = prevGlobal;
  prevGlobal = global;

  if (!oldGlobal) return;

  const oldSharedSettings = selectSharedSettings(oldGlobal);
  const sharedSettings = selectSharedSettings(global);

  const performance = sharedSettings.performance;
  const prevPerformance = oldSharedSettings.performance;
  const peerColors = global.peerColors;
  const prevPeerColors = oldGlobal.peerColors;

  if (peerColors && peerColors !== prevPeerColors) {
    updatePeerColors(peerColors.general);
  }

  if (performance !== prevPerformance) {
    requestMutation(() => {
      applyPerformanceSettings(performance);
    });
  }

  if (sharedSettings.theme !== oldSharedSettings.theme) {
    const withAnimation = false;
    switchTheme(sharedSettings.theme, withAnimation);
  }

  if (sharedSettings.language !== oldSharedSettings.language) {
    oldSetLanguage(sharedSettings.language as LangCode);
  }

  if (sharedSettings.timeFormat !== oldSharedSettings.timeFormat) {
    setLocalizedTimeFormat(sharedSettings.timeFormat);
    setLegacyTimeFormat(sharedSettings.timeFormat);
  }

  if (sharedSettings.messageTextSize !== oldSharedSettings.messageTextSize) {
    document.documentElement.style.setProperty(
      '--composer-text-size', `${Math.max(sharedSettings.messageTextSize, IS_IOS ? 16 : 15)}px`,
    );
    document.documentElement.style.setProperty('--message-meta-height',
      `${Math.floor(sharedSettings.messageTextSize * 1.3125)}px`);
    document.documentElement.style.setProperty('--message-text-size', `${sharedSettings.messageTextSize}px`);
    document.documentElement.setAttribute('data-message-text-size', sharedSettings.messageTextSize.toString());
  }

  if (sharedSettings.canDisplayChatInTitle !== oldSharedSettings.canDisplayChatInTitle) {
    updatePageTitle();
  }

  if (sharedSettings.shouldForceHttpTransport !== oldSharedSettings.shouldForceHttpTransport) {
    callApi('setForceHttpTransport', Boolean(sharedSettings.shouldForceHttpTransport));
  }

  if (sharedSettings.shouldAllowHttpTransport !== oldSharedSettings.shouldAllowHttpTransport) {
    callApi('setAllowHttpTransport', Boolean(sharedSettings.shouldAllowHttpTransport));
    if (!sharedSettings.shouldAllowHttpTransport && sharedSettings.shouldForceHttpTransport) {
      global = updateSharedSettings(global, {
        shouldForceHttpTransport: false,
      });
      setGlobal(global);
    }
  }

  if (sharedSettings.shouldDebugExportedSenders !== oldSharedSettings.shouldDebugExportedSenders) {
    updateShouldDebugExportedSenders();
  }

  if (sharedSettings.shouldCollectDebugLogs !== oldSharedSettings.shouldCollectDebugLogs) {
    updateShouldEnableDebugLog();
  }

  const rasiTech = selectRasiTechSettings(global);
  const prevRasiTech = selectRasiTechSettings(oldGlobal);
  if (rasiTech.fakePremium !== prevRasiTech.fakePremium) {
    void setIsPremium({ isPremium: isModFeatureEnabled('fakePremium') });
  }
  if (rasiTech.lightweight !== prevRasiTech.lightweight) {
    const nextPerf = rasiTech.lightweight
      ? INITIAL_PERFORMANCE_STATE_MIN
      : INITIAL_PERFORMANCE_STATE_MAX;
    requestMutation(() => {
      document.body.classList.toggle('nm-lite', Boolean(rasiTech.lightweight));
      document.body.classList.add('nm-whatsapp');
      applyPerformanceSettings(nextPerf);
    });
    setGlobal(updateSharedSettings(getGlobal(), {
      performance: nextPerf,
      animationLevel: rasiTech.lightweight ? 0 : 1,
    }));
  }
});

addActionHandler('updateShouldEnableDebugLog', (global): ActionReturnType => {
  const settings = selectSharedSettings(global);

  if (settings.shouldCollectDebugLogs) {
    setShouldEnableDebugLog(true);
    initDebugConsole();
  } else {
    setShouldEnableDebugLog(false);
    disableDebugConsole();
  }
});

addActionHandler('updateShouldDebugExportedSenders', (global): ActionReturnType => {
  const settings = selectSharedSettings(global);
  callApi('setShouldDebugExportedSenders', Boolean(settings.shouldDebugExportedSenders));
});

addActionHandler('setSettingOption', (global, actions, payload): ActionReturnType => {
  return replaceSettings(global, payload);
});

addActionHandler('setSharedSettingOption', (global, actions, payload): ActionReturnType => {
  let sanitized = payload;
  const uid = getRasiTechAccessUserId();
  let featureDenied = false;

  if (payload?.rasiTech) {
    const nextRasiTech = { ...payload.rasiTech };
    PAID_MOD_FEATURES.forEach((key) => {
      if (nextRasiTech[key] && !canUsePaidModFeature(key)) {
        nextRasiTech[key] = false;
        featureDenied = true;
      }
    });
    sanitized = { ...payload, rasiTech: nextRasiTech };
  }

  if (payload?.rasiTechModules?.privateMode) {
    const pm = { ...payload.rasiTechModules.privateMode };
    PAID_PRIVATE_MODE_KEYS.forEach((key) => {
      if (pm[key] && !canUsePaidPrivateMode(key)) {
        pm[key] = false;
        featureDenied = true;
      }
    });
    sanitized = {
      ...sanitized,
      rasiTechModules: {
        ...sanitized?.rasiTechModules,
        privateMode: pm,
      },
    };
  }

  if (featureDenied && uid) {
    void logRasiActivity(uid, 'feature_denied', 'Attempted to enable paid-only RasiTech feature');
  }

  global = updateSharedSettings(global, sanitized);

  const rasiTechSettings = selectRasiTechSettings(global);
  setModFeatureCache(rasiTechSettings);

  if (sanitized?.rasiTechModules) {
    const modules = selectRasiTechModules(global);
    setRasiTechModulesCache(modules);
    if (sanitized.rasiTechModules.privateMode) {
      const pm = modules.privateMode;
      global = updateSharedSettings(global, {
        rasiTech: {
          ...selectRasiTechSettings(global),
          antiDelete: pm.restoreDeletedMessages,
          antiEditHistory: pm.showEditedWithOriginal,
          unrestrictMedia: true,
        },
      });
      setModFeatureCache(selectRasiTechSettings(global));
    }
  }

  if (sanitized?.rasiTech) {
    forceUpdateCache();
  }

  if (payload?.rasiTech?.fakePremium !== undefined) {
    void setIsPremium({ isPremium: isModFeatureEnabled('fakePremium') });
  }

  return global;
});

addActionHandler('updatePerformanceSettings', (global, actions, payload): ActionReturnType => {
  const settings = selectSharedSettings(global);
  global = updateSharedSettings(global, {
    performance: {
      ...settings.performance,
      ...payload,
    },
    wasAnimationLevelSetManually: true,
  });

  return global;
});

addActionHandler('setThemeSettings', (global, actions, payload): ActionReturnType => {
  const { theme, ...settings } = payload;

  return updateThemeSettings(global, theme, settings);
});

addActionHandler('requestNextFoldersAction', (global, actions, payload): ActionReturnType => {
  const { foldersAction, tabId = getCurrentTabId() } = payload;
  return updateTabState(global, {
    nextFoldersAction: foldersAction,
  }, tabId);
});

addActionHandler('openLeftColumnContent', (global, actions, payload): ActionReturnType => {
  const { contentKey = LeftColumnContent.ChatList, tabId = getCurrentTabId() } = payload;
  const tabState = selectTabState(global, tabId);
  return updateTabState(global, {
    leftColumn: {
      ...tabState.leftColumn,
      contentKey,
    },
  }, tabId);
});

addActionHandler('openSettingsScreen', (global, actions, payload): ActionReturnType => {
  const { screen, tabId = getCurrentTabId() } = payload;
  const tabState = selectTabState(global, tabId);

  actions.loadPrivacySettings({ skipIfCached: true });
  // Force settings only if new screen is passed, do not on resets
  if (payload.screen !== undefined) {
    actions.openLeftColumnContent({ contentKey: LeftColumnContent.Settings, tabId });
    // Fullscreen settings: close open chat so middle column hides
    actions.openChat({ id: undefined, tabId }, { forceOnHeavyAnimation: true });
  }
  return updateTabState(global, {
    leftColumn: {
      ...tabState.leftColumn,
      settingsScreen: screen || SettingsScreens.Main,
    },
    isLeftColumnShown: payload.screen !== undefined ? true : tabState.isLeftColumnShown,
  }, tabId);
});

addActionHandler('openEditChatFolder', (global, actions, payload): ActionReturnType => {
  const { folderId, isOnlyInvites, tabId = getCurrentTabId() } = payload;

  const chatFolder = selectChatFolder(global, folderId);
  if (!chatFolder) return;

  actions.openSettingsScreen({
    screen: isOnlyInvites ? SettingsScreens.FoldersEditFolderInvites : SettingsScreens.FoldersEditFolderFromChatList,
    tabId,
  });
  actions.requestNextFoldersAction({
    foldersAction: {
      type: 'editFolder',
      payload: chatFolder,
    },
    tabId,
  });
});

addActionHandler('openShareChatFolderModal', (global, actions, payload): ActionReturnType => {
  const {
    folderId, url, noRequestNextScreen, tabId = getCurrentTabId(),
  } = payload;

  const chatFolder = selectChatFolder(global, folderId);
  const isChatList = chatFolder?.isChatList;
  if (isChatList && !noRequestNextScreen) {
    actions.openEditChatFolder({ folderId, isOnlyInvites: true, tabId });
    return undefined;
  }

  if (!noRequestNextScreen) actions.openSettingsScreen({ screen: SettingsScreens.FoldersShare, tabId });

  return updateTabState(global, {
    shareFolderScreen: {
      folderId,
      isFromSettings: Boolean(noRequestNextScreen),
      url,
    },
  }, tabId);
});

addActionHandler('closeShareChatFolderModal', (global, actions, payload): ActionReturnType => {
  const { tabId = getCurrentTabId() } = payload || {};

  actions.openSettingsScreen({ screen: undefined, tabId });

  return updateTabState(global, {
    shareFolderScreen: undefined,
  }, tabId);
});

addActionHandler('openPasskeyModal', (global, actions, payload): ActionReturnType => {
  const { tabId = getCurrentTabId() } = payload || {};
  return updateTabState(global, {
    isPasskeyModalOpen: true,
  }, tabId);
});

addTabStateResetterAction('closePasskeyModal', 'isPasskeyModalOpen');
